/** @jsxImportSource @emotion/react */

import { FC, useEffect, useRef, useState } from "react";
import logo from "../../assets/logo.svg";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "../../../../hooks/useCSSRulesWithTheme";
import Button from "../../../Button";
import { useNavigate } from "react-router-dom";
import {
  HomeLocation,
  LoginLocation,
  RegisterLocation,
} from "../../../../Locations";
import { useAuth } from "../../../../hooks/useAuth";
import Typography from "../../../Typography";
import { useUIStore } from "../../../../hooks/useUIStore";
import NewMessage from "../../../NewMessage";
import Container from "../../../Grid/Container";
import { ColorNames, theme } from "../../../../theme";
import { IconSizes } from "../../../Icons/styles/iconSizes";
import Avatar from "../../../Avatar";
import HR from "../../../HorizontalLine";
import { IconNames } from "../../../Icons/styles/iconNames";
import Icon from "../../../Icons";

export const headerHeight = 75;

const getCSSRules: CSSRulesResolver = ({ ...props }) => ({
  header: {
    position: "sticky",
    top: 0,
    height: headerHeight,
    zIndex: 300,
    backgroundColor: `${props.colors.White[100]}`,
    boxShadow: theme.boxShadow.default,
  },
  container: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: {
    cursor: "pointer",
  },
  dropdownMenu: {
    position: "absolute",
    top: headerHeight,
    right: 12,
    backgroundColor: `${props.colors.White[100]}`,
    width: 225,
    borderRadius: 5,
    border: `2px solid ${props.colors.Green[100]}`,
    padding: 10,
    zIndex: 300,
  },
  menuItem: {
    cursor: "pointer",
    padding: "10px 5px",
    display: "flex",
    alignItems: "center",
    "& > div": {
      marginRight: 10,
    },
    "&:hover": {
      backgroundColor: `${props.colors.LightGrey[80]}`,
    },
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  profileName: {
    cursor: "pointer",
  },
  userName: {
    cursor: "default",
    display: "flex",
    alignItems: "center",
    "& > div": {
      marginRight: 10,
    },
  },
});

const Header: FC = (props) => {
  const styles = useCSSRulesWithTheme(getCSSRules, props);
  const navigate = useNavigate();
  const ref: any = useRef(null);
  const navigateLogin = () => {
    navigate(LoginLocation);
  };
  const navigateRegister = () => {
    navigate(RegisterLocation);
  };
  const { openProfileModal } = useUIStore();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleClickDropdown = () => {
    setIsMenuOpen((isMenuOpen) => !isMenuOpen);
  };

  const handleOpenProfileModal = () => {
    openProfileModal();
    setIsMenuOpen(false);
  };

  // close the dropdown menu when clicked outside
  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      // If the menu is open and the clicked target is not within the menu,
      // then close the menu
      if (isMenuOpen && ref.current && !ref.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isMenuOpen]);

  const { isLoggedIn, user, signOut } = useAuth();

  return (
    <div css={styles.header}>
      <Container css={styles.container}>
        <img
          src={logo}
          css={styles.logo}
          onClick={() => navigate(HomeLocation)}
        />
        <div>
          {isLoggedIn ? (
            <>
              <Avatar
                name={user.nome || user.username}
                size={IconSizes.ExtraLarge}
                color={ColorNames.GREEN}
                filled
                onClick={handleClickDropdown}
              />
              <div ref={ref}>
                {isMenuOpen && (
                  <div css={styles.dropdownMenu}>
                    <div css={styles.userName}>
                      <Avatar
                        name={user.nome || user.username}
                        color={ColorNames.GREEN}
                        size={IconSizes.ExtraLarge}
                        filled
                      />
                      <div>
                        <Typography
                          variant="textMedium"
                          component="div"
                          align="left"
                          weight="bold"
                        >
                          {user.nome || user.username}
                        </Typography>
                        <Typography
                          variant="textSmall"
                          component="div"
                          align="left"
                        >
                          {user.email}
                        </Typography>
                      </div>
                    </div>
                    <HR color={ColorNames.GREEN} />
                    <Typography
                      variant="textMedium"
                      align="left"
                      component="div"
                      css={styles.menuItem}
                      onClick={handleOpenProfileModal}
                    >
                      Perfil
                    </Typography>
                    <div css={styles.menuItem}>
                      <Typography
                        variant="textMedium"
                        align="left"
                        component="div"
                        onClick={signOut}
                      >
                        Logout
                      </Typography>
                      <Icon name={IconNames.Logout} size={IconSizes.Small} />
                    </div>
                  </div>
                )}
                <NewMessage message="você tem uma nova notificação" />
              </div>
            </>
          ) : (
            <div css={styles.buttons}>
              <Button
                text="Cadastro"
                style="primaryLink"
                onClick={navigateRegister}
              />
              <Button
                text="Login"
                style="primaryLink"
                onClick={navigateLogin}
              />
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Header;
